import { Dtos, NoteTopicId, Relations } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import { adminNoteTopicsQueryKey } from "../note.keys";
import { AdminNoteTopic } from "./types";

export const useCreateAdminNoteTopic = () => {
	const queryClient = useQueryClient();
	const { post } = useRequest();

	const mutation = useMutation({
		mutationFn: async (data: Dtos.Admin.AdminNoteTopic.Create.Request) =>
			post<Dtos.Admin.AdminNoteTopic.Create.Response>("/v2/admin/admin-notes/note-topics", data),
		onMutate: async ({ name, relationType, relationId }) => {
			const relation = { relationType, relationId } as Relations.Notes;
			await queryClient.cancelQueries({ queryKey: adminNoteTopicsQueryKey(relation) });
			const previousTopics = queryClient.getQueryData<AdminNoteTopic[]>(
				adminNoteTopicsQueryKey(relation),
			);
			const tempTopicId = Math.random().toString(36).substring(7) as NoteTopicId;
			queryClient.setQueryData<AdminNoteTopic[]>(
				adminNoteTopicsQueryKey(relation),
				(oldTopics: AdminNoteTopic[] | undefined) => {
					if (!oldTopics) return [];
					return [
						...oldTopics,
						{
							id: tempTopicId,
							name,
							summary: "",
							...relation,
							isDefault: false,
							isArchived: false,
							notes: [],
							createdAt: new Date().toISOString(),
							updatedAt: new Date().toISOString(),
						},
					];
				},
			);
			return { previousTopics, tempTopicId, relation };
		},
		onSuccess: (newTopic, inputData, context) => {
			const { relationId, relationType } = inputData;
			const relation = { relationId, relationType } as Relations.Notes;
			queryClient.setQueryData(
				adminNoteTopicsQueryKey(relation),
				(oldTopics: AdminNoteTopic[] | undefined) => {
					if (!oldTopics) return [newTopic];
					return oldTopics.map((topic) => {
						if (topic.id === context?.tempTopicId) {
							return newTopic;
						}
						return topic;
					});
				},
			);
		},
	});

	return {
		createAdminNoteTopic: mutation.mutate,
		createAdminNoteTopicIsLoading: mutation.isPending,
	};
};
